import React, { useState, useEffect } from 'react';
import './Login.css';
import AuditorioReserva from './AuditorioReserva';
import config, { getApiUrl } from './config';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalconfig';

// Inicializar la aplicación MSAL
const msalInstance = new PublicClientApplication(msalConfig);

function generarCaptcha(longitud = config.CAPTCHA.LENGTH) {
  const caracteres = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789/*-+!#$%&/()=';
  let captcha = '';
  for (let i = 0; i < longitud; i++) {
    captcha += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
  }
  return captcha;
}

export default function Login() {
  const [usuario, setUsuario] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaGenerado, setCaptchaGenerado] = useState(generarCaptcha());
  const [mensaje, setMensaje] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(config.CAPTCHA.REFRESH_INTERVAL / 1000);
  const [loginExitoso, setLoginExitoso] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [msalAccount, setMsalAccount] = useState(null);
  const [msalInitialized, setMsalInitialized] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usuario || !contrasena || !captcha) {
      setMensaje(config.MENSAJES.CAMPOS_INCOMPLETOS);
    } else if (captcha !== captchaGenerado) {
      setMensaje(config.MENSAJES.CAPTCHA_INCORRECTO);
      setCaptcha('');
      setCaptchaGenerado(generarCaptcha());
    } else {
      // Validar con backend
      try {
        const response = await fetch(getApiUrl(config.ENDPOINTS.LOGIN), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ usuario, contrasena })
        });
        const data = await response.json();
        if (data.success) {
          setMensaje(config.MENSAJES.LOGIN_EXITOSO);
          
          // Verificar si el usuario está en la tabla RESERVAS_ADMIN
          try {
            console.log("🔍 Verificando usuario en RESERVAS_ADMIN:", usuario);
            
            const adminResponse = await fetch(getApiUrl(config.ENDPOINTS.CHECK_ADMIN), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ usuario: usuario })
            });
            const adminData = await adminResponse.json();
            console.log("🔍 Respuesta de verificación en RESERVAS_ADMIN:", adminData);
            
            // Si el usuario está en la tabla RESERVAS_ADMIN, mostrar el botón
            if (adminData.success && adminData.is_admin) {
              setIsAdmin(true);
              console.log("✅ Usuario es admin, se mostrará el botón Ver Reportes");
            } else {
              setIsAdmin(false);
              console.log("❌ Usuario NO es admin, no se mostrará el botón Ver Reportes");
            }
          } catch (error) {
            console.error("Error al verificar si el usuario está en RESERVAS_ADMIN:", error);
            setIsAdmin(false); // Por defecto, no mostrar el botón si hay error
          }
          
          setTimeout(() => {
            setLoginExitoso(true);
          }, 1500);
        } else {
          setMensaje(data.message || config.MENSAJES.ERROR_AUTENTICACION);
        }
      } catch (error) {
        console.error("Error en inicio de sesión:", error);
        setMensaje(error.message || config.MENSAJES.ERROR_CONEXION);
      }
    }
  };

  const recargarCaptcha = () => {
    setCaptchaGenerado(generarCaptcha());
    setCaptcha('');
    setMensaje('');
    setSecondsLeft(config.CAPTCHA.REFRESH_INTERVAL / 1000);
  };

  const handleLogout = () => {
    setLoginExitoso(false);
    setIsAdmin(false);
    setUsuario('');
    setContrasena('');
    setCaptcha('');
    setMensaje('');
    setCaptchaGenerado(generarCaptcha());
    setSecondsLeft(config.CAPTCHA.REFRESH_INTERVAL / 1000);
    setLogoError(false);
    setMsalAccount(null);
  };
  
  // Función para iniciar sesión con Microsoft
  const handleMicrosoftLogin = async () => {
    try {
      // Verificar que MSAL esté inicializado
      if (!msalInitialized) {
        setMensaje("Inicializando autenticación de Microsoft, por favor espere...");
        return;
      }

      console.log("Iniciando inicio de sesión con Microsoft...");
      console.log("Configuración MSAL:", {
        clientId: msalConfig.auth.clientId,
        authority: msalConfig.auth.authority,
        redirectUri: msalConfig.auth.redirectUri
      });
      
      // Limpiar cualquier sesión anterior
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log("Cuentas existentes encontradas:", accounts);
        // Opcional: puedes usar estas cuentas en lugar de hacer login de nuevo
      }
      
      // Usar loginRequest que contiene los scopes definidos
      const loginResponse = await msalInstance.loginPopup({
        ...loginRequest,
        redirectUri: msalConfig.auth.redirectUri
      });
      
      console.log("Respuesta de inicio de sesión:", loginResponse);
      
      if (loginResponse && loginResponse.account) {
        // Guardar la cuenta de Microsoft
        setMsalAccount(loginResponse.account);
        // Usar el email como nombre de usuario
        setUsuario(loginResponse.account.username);
        
        // Verificar si el usuario está en la tabla RESERVAS_ADMIN
        try {
          // Usar el email del usuario para verificar en la tabla RESERVAS_ADMIN
          const userEmail = loginResponse.account.username;
          console.log("🔍 Verificando email en RESERVAS_ADMIN:", userEmail);
          
          const adminResponse = await fetch(getApiUrl(config.ENDPOINTS.CHECK_ADMIN), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ usuario: userEmail })
          });
          const adminData = await adminResponse.json();
          console.log("🔍 Respuesta de verificación en RESERVAS_ADMIN:", adminData);
          
          // Si el usuario está en la tabla RESERVAS_ADMIN, mostrar el botón
          if (adminData.success && adminData.is_admin) {
            setIsAdmin(true);
            console.log("✅ Usuario es admin, se mostrará el botón Ver Reportes");
          } else {
            setIsAdmin(false);
            console.log("❌ Usuario NO es admin, no se mostrará el botón Ver Reportes");
          }
        } catch (error) {
          console.error("Error al verificar si el usuario está en RESERVAS_ADMIN:", error);
          setIsAdmin(false); // Por defecto, no mostrar el botón si hay error
        }
        
        // Mostrar mensaje de éxito
        setMensaje(config.MENSAJES.LOGIN_EXITOSO);
        // Redirigir después de un breve retraso
        setTimeout(() => {
          setLoginExitoso(true);
        }, 1500);
      }
    } catch (error) {
      console.error("Error durante el login con Microsoft:", error);
      // Mensaje de error más descriptivo
      setMensaje(`Error al iniciar sesión con Microsoft: ${error.message || "Intente nuevamente."}`);
    }
  };

  const handleLogoError = () => { setLogoError(true);};

  // Inicializar MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        setMsalInitialized(true);
        console.log("MSAL inicializado correctamente");
        
        // Verificar si hay cuentas en caché
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          console.log("Cuentas en caché encontradas:", accounts);
        }
      } catch (error) {
        console.error("Error al inicializar MSAL:", error);
      }
    };

    initializeMsal();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          recargarCaptcha();
          return config.CAPTCHA.REFRESH_INTERVAL / 1000;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Si el login fue exitoso, mostrar la página correspondiente
  if (loginExitoso) {
    return <AuditorioReserva 
      onLogout={handleLogout} 
      usuarioLogueado={usuario} 
      isAdmin={isAdmin} 
    />;
  }

  return (
    <div className="login-bg" style={{ backgroundImage: `url(${config.IMAGES.BACKGROUND})` }}>
      {/* Encabezado con logo animado */}
      <header className="login-header">
        <div className="logo-container">
          <img 
            src="/imagenes/logo_usb_blanco.png" 
            alt="Logo USB" 
            onError={(e) => {
              console.warn('No se pudo cargar el logo USB:', e.target.src);
              e.target.style.display = 'none';
            }}
          />
        </div>
      </header>
      
      {/* Título principal */}
      <div className="login-title">
        <h1>Reserva de Auditorios</h1>
      </div>
      
      {/* Contenedor del formulario */}
      <div className="login-container">
        <form className="login-form" onSubmit={handleSubmit}>
          {/*
          <div className="login-logo">
            {!logoError ? (
              <img 
                src={config.IMAGES.LOGO} 
                alt="Logo de la empresa" 
                className="logo-image"
                onError={handleLogoError}
              />
            ) : (
              <span>LOGO</span>
            )}
          </div>
          <input
            className="login-input"
            type="text"
            placeholder="Usuario"
            value={usuario}
            onChange={e => setUsuario(e.target.value.toLowerCase())}
          />
          <input
            className="login-input"
            type="password"
            placeholder="Contraseña"
            value={contrasena}
            onChange={e => setContrasena(e.target.value)}
          />
          <div className="login-captcha-row">
            <span className="login-captcha">{captchaGenerado}</span>
            <div
              className="login-captcha-timer"
              title={`Actualiza en ${secondsLeft}s`}
              aria-label={`Captcha se actualiza en ${secondsLeft} segundos`}
              role="img"
              style={{
                background: `conic-gradient(${config.ESTILOS.COLORS.PRIMARY} ${(100 * ((config.CAPTCHA.REFRESH_INTERVAL / 1000) - secondsLeft)) / (config.CAPTCHA.REFRESH_INTERVAL / 1000)}%, ${config.ESTILOS.COLORS.GRAY_LIGHT} 0)`
              }}
            >
              <span className="login-captcha-timer-text">{secondsLeft}</span>
            </div>
          </div>
          <input
            className="login-input"
            type="text"
            placeholder="Escribe el captcha"
            value={captcha}
            onChange={e => setCaptcha(e.target.value.toUpperCase())}
            autoComplete="off"
          />
          <button className="login-btn" type="submit">
            Iniciar sesión
          </button>

          */}
          
          <button 
            className="login-btn microsoft-btn" 
            type="button" 
            onClick={handleMicrosoftLogin}
            style={{ 
              backgroundColor: '#C6A836', 
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{ marginRight: '10px' }}>
              <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            </span>
            Iniciar con Microsoft
          </button>
          {mensaje && <div className="login-msg">{mensaje}</div>}
        </form>
      </div>
    </div>
  );
}